import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchMaintenanceMasters,
  createMaintenanceRequest,
  updateMaintenanceRequest,
} from "../../api/maintenanceApi";
import { masterSelectOptions } from "../../../memberships/utils/masterLookupUtils";
import {
  GlobalTextField,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalButton,
  GlobalDateField,
} from "../../../../global/forms/GlobalFormFields";
import { gfIsValidISODateRange } from "../../../../global/forms/globalFormValidation";
import { compressAttachmentIfImage } from "../../../../utils/compressImage";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import OfficeStaffAssignTypeahead from "../control/OfficeStaffAssignTypeahead";
import "../../styles/createMaintenanceRequest.css";

const toISODate = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [mm, dd, yyyy] = raw.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function rowToEditForm(row) {
  return {
    maintenance_type_id: String(row.maintenance_type_id ?? ""),
    priority_id: String(row.priority_id ?? ""),
    asset_area: row.asset_area || "",
    issue_description: row.issue_description || "",
    assigned_to: row.assigned_to != null && row.assigned_to !== "" ? String(row.assigned_to) : "",
    assigned_to_name: row.assigned_to_name || "",
    start_date: toISODate(row.start_date),
    completion_date: toISODate(row.completion_date),
    cost_incurred:
      row.cost_incurred != null && row.cost_incurred !== "" ? String(row.cost_incurred) : "",
    attachment: null,
  };
}

const EMPTY_CREATE = {
  maintenance_type_id: "",
  issue_description: "",
  asset_area: "",
  priority_id: "",
  start_date: "",
  completion_date: "",
  cost_incurred: "",
  attachment: null,
};

/**
 * @param {boolean} [mastersFromParent] — when true, use `masters` / `mastersLoadError` from the page (single fetch, no duplicate errors).
 * @param {object|null} [masters] — parent-loaded masters object, or null while loading / on failure.
 * @param {string} [mastersLoadError] — parent fetch error (shown once at page level; form shows a short fallback only).
 * @param {object|null} [editingRequest] — list row to edit in place; clears create draft when set.
 * @param {() => void} [onCancelEdit] — called when user cancels edit mode.
 */
const CreateMaintenanceRequest = ({
  onNotify,
  onRequestCreated,
  mastersFromParent = false,
  masters: parentMasters = null,
  mastersLoadError = "",
  editingRequest = null,
  onCancelEdit,
}) => {
  const [masters, setMasters] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_CREATE);
  const attachmentInputRef = useRef(null);

  const isEditMode = Boolean(editingRequest?.id);

  useEffect(() => {
    if (mastersFromParent) return;
    let cancelled = false;
    (async () => {
      try {
        const mData = await fetchMaintenanceMasters();
        if (!cancelled) setMasters(mData);
      } catch (e) {
        if (!cancelled) onNotify(e.message || "Could not load maintenance data.", { variant: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mastersFromParent, onNotify]);

  useEffect(() => {
    if (editingRequest?.id) {
      setFormData(rowToEditForm(editingRequest));
    } else {
      setFormData({ ...EMPTY_CREATE });
    }
  }, [editingRequest]);

  const setFormField = useCallback((name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleFileChange = useCallback(async (e) => {
    const { files } = e.target;
    const file = files && files.length > 0 ? files[0] : null;
    if (!file) {
      setFormField("attachment", null);
      return;
    }
    try {
      const uploadFile = await compressAttachmentIfImage(file);
      setFormField("attachment", uploadFile);
    } catch {
      e.target.value = "";
      setFormField("attachment", null);
    }
  }, [setFormField]);

  const handleReset = useCallback(() => {
    if (submitting) return;
    setFormData({ ...EMPTY_CREATE });
  }, [submitting]);

  const handleCancelEdit = useCallback(() => {
    if (submitting) return;
    onCancelEdit?.();
  }, [submitting, onCancelEdit]);

  const handleSubmitCreate = useCallback(
    async (e) => {
      e.preventDefault();
      if (submitting) return;

      if (!gfIsValidISODateRange(formData.start_date, formData.completion_date)) {
        onNotify("End date cannot be before start date.", { variant: "error" });
        return;
      }

      if (!formData.attachment) {
        onNotify("Attachment is required.", { variant: "error" });
        return;
      }

      setSubmitting(true);
      try {
        const submitData = new FormData();
        ["maintenance_type_id", "issue_description", "asset_area", "priority_id"].forEach((key) => {
          const v = formData[key];
          if (v !== undefined && v !== null) submitData.append(key, v);
        });
        if (formData.start_date) submitData.append("start_date", formData.start_date);
        if (formData.completion_date) submitData.append("completion_date", formData.completion_date);
        if (formData.cost_incurred !== "" && formData.cost_incurred != null) {
          submitData.append("cost_incurred", formData.cost_incurred);
        }
        submitData.append("file", formData.attachment);
        await createMaintenanceRequest(submitData);
        setFormData({ ...EMPTY_CREATE });
        onNotify("Maintenance request created successfully!");
        onRequestCreated?.();
      } catch (error) {
        onNotify(error.message || "Failed to create maintenance request", { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
    [formData, submitting, onNotify, onRequestCreated],
  );

  const handleSubmitEdit = useCallback(
    async (e) => {
      e.preventDefault();
      if (submitting || !editingRequest?.id) return;

      if (!gfIsValidISODateRange(formData.start_date, formData.completion_date)) {
        onNotify("End date cannot be before start date.", { variant: "error" });
        return;
      }

      setSubmitting(true);
      try {
        const body = {
          maintenance_type_id: formData.maintenance_type_id,
          asset_area: String(formData.asset_area || "").trim(),
          issue_description: String(formData.issue_description || "").trim(),
          priority_id: formData.priority_id || null,
        };
        if (formData.assigned_to) {
          body.assigned_to = formData.assigned_to;
        } else {
          body.assigned_to = null;
        }
        if (formData.start_date) body.start_date = formData.start_date;
        if (formData.completion_date) body.completion_date = formData.completion_date;
        if (formData.cost_incurred !== "" && formData.cost_incurred != null) {
          body.cost_incurred = formData.cost_incurred;
        }

        await updateMaintenanceRequest(editingRequest.id, body);
        onNotify("Maintenance request updated successfully!");
        onCancelEdit?.();
        onRequestCreated?.();
      } catch (error) {
        onNotify(error.message || "Failed to update maintenance request", { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, editingRequest?.id, formData, onNotify, onCancelEdit, onRequestCreated],
  );

  const resolvedMasters = mastersFromParent ? parentMasters : masters;
  const maintenanceTypes = resolvedMasters?.maintenanceTypes || [];
  const priorities = resolvedMasters?.priorityLevels || [];

  if (mastersFromParent) {
    if (mastersLoadError) {
      return (
        <div className="create-maintenance-request-loading" role="status">
          Create form unavailable (lookups did not load).
        </div>
      );
    }
    if (!resolvedMasters) {
      return (
        <div className="create-maintenance-request-loading">Loading maintenance data...</div>
      );
    }
  } else if (!masters) {
    return (
      <div className="create-maintenance-request-loading">Loading maintenance data...</div>
    );
  }

  const dateRangeOk = gfIsValidISODateRange(formData.start_date, formData.completion_date);
  const costOk =
    !formData.cost_incurred ||
    (Number(formData.cost_incurred) >= 0 && String(formData.cost_incurred).length <= 8);

  const editDisabled =
    submitting ||
    !dateRangeOk ||
    !formData.maintenance_type_id ||
    !String(formData.asset_area || "").trim() ||
    !costOk;

  const createDisabled =
    submitting ||
    !dateRangeOk ||
    !costOk ||
    !formData.maintenance_type_id ||
    !formData.priority_id ||
    !String(formData.asset_area || "").trim() ||
    !String(formData.issue_description || "").trim();

  return (
    <div className="create-maintenance-request-form">
      <form
        key={isEditMode ? `edit-${editingRequest.id}` : "create"}
        onSubmit={isEditMode ? handleSubmitEdit : handleSubmitCreate}
      >
        <GlobalFormShell
          legend={isEditMode ? `Edit maintenance request #${editingRequest.id}` : "Create maintenance request"}
          showAlert={false}
          alertText=""
          alertId="create-maintenance-request-alert"
          actions={
            <>
              <GlobalButton
                type="submit"
                variant="primary"
                disabled={isEditMode ? editDisabled : createDisabled}
                loading={submitting}
              >
                {submitting ? "Saving…" : isEditMode ? "Save changes" : "Submit Request"}
              </GlobalButton>
              {isEditMode ? (
                <GlobalButton
                  type="button"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  disabled={submitting}
                >
                  Cancel edit
                </GlobalButton>
              ) : (
                <GlobalButton type="button" variant="ghost" onClick={handleReset} disabled={submitting}>
                  Clear form
                </GlobalButton>
              )}
            </>
          }
        >
          <GlobalSelectField
            label="Maintenance Type"
            value={formData.maintenance_type_id}
            onChange={(value) => setFormField("maintenance_type_id", value)}
            required
            placeholderOption="Select type..."
            options={masterSelectOptions(maintenanceTypes, {
              labelKey: "type_name",
              selectedValue: formData.maintenance_type_id,
            })}
          />

          <GlobalSelectField
            label="Priority"
            value={formData.priority_id}
            onChange={(value) => setFormField("priority_id", value)}
            required
            placeholderOption="Select priority..."
            options={masterSelectOptions(priorities, {
              labelKey: "priority_name",
              selectedValue: formData.priority_id,
            })}
          />

          <GlobalTextField
            label="Asset / Area"
            value={formData.asset_area}
            onChange={(value) => setFormField("asset_area", value)}
            placeholder="e.g., Room 101, HVAC System, Main Entrance..."
            required
          />

          <GlobalTextAreaField
            classNames={{ fieldWrap: "gff-field gff-field--full" }}
            label="Issue Description"
            value={formData.issue_description}
            onChange={(value) => setFormField("issue_description", value)}
            rows={4}
            placeholder="Describe the maintenance issue in detail..."
            required={!isEditMode}
          />

          <GlobalDateField
            label="Start date"
            value={formData.start_date || ""}
            onChange={(value) => {
              const startISO = toISODate(value);
              setFormData((prev) => {
                const next = { ...prev, start_date: startISO };
                if (next.completion_date && next.completion_date < startISO) {
                  next.completion_date = "";
                  onNotify("Completion date cannot be before start date.", { variant: "error" });
                }
                return next;
              });
            }}
            max={formData.completion_date || ""}
            error={
              !gfIsValidISODateRange(formData.start_date, formData.completion_date)
                ? "Start date must be on or before completion date"
                : ""
            }
          />
          <GlobalDateField
            label="Completion date"
            value={formData.completion_date || ""}
            onChange={(value) => {
              const completionISO = toISODate(value);
              setFormData((prev) => {
                const next = { ...prev, completion_date: completionISO };
                if (next.start_date && completionISO && completionISO < next.start_date) {
                  next.completion_date = "";
                  onNotify("Completion date cannot be before start date.", { variant: "error" });
                }
                return next;
              });
            }}
            min={formData.start_date || ""}
            error={
              !gfIsValidISODateRange(formData.start_date, formData.completion_date)
                ? "Completion date must be on or after start date"
                : ""
            }
          />
          <GlobalTextField
            label="Cost incurred (Rs)"
            type="number"
            step="0.01"
            maxLength={8}
            min="0"
            value={formData.cost_incurred || ""}
            onChange={(value) => {
              const cleanValue = value.replace(/[^0-9.]/g, "");
              const limitedValue = cleanValue.slice(0, 8);
              if (limitedValue !== value) return;
              setFormField("cost_incurred", limitedValue);
            }}
            error={
              formData.cost_incurred &&
              (Number(formData.cost_incurred) < 0 || String(formData.cost_incurred).length > 8)
                ? "Cost must be positive and at most 8 characters (Rs)"
                : ""
            }
            placeholder="Optional"
          />

          {isEditMode ? (
            <OfficeStaffAssignTypeahead
              userId={formData.assigned_to}
              displayName={formData.assigned_to_name}
              onChange={(id, name) =>
                setFormData((prev) => ({
                  ...prev,
                  assigned_to: id,
                  assigned_to_name: name,
                }))
              }
              label="Assigned to (office staff)"
            />
          ) : null}

          {!isEditMode ? (
            <div className="gff-field gff-field--full cmr-file-upload">
              <span className="gff-label">
                Attachment <span aria-hidden="true">*</span>
              </span>
              <div className="cmr-file-upload__row">
                <input
                  ref={attachmentInputRef}
                  id="maintenance_attachment_file"
                  name="file"
                  type="file"
                  className="cmr-file-upload__native"
                  onChange={handleFileChange}
                  accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                  disabled={submitting}
                  aria-label="Choose attachment file"
                />
                <GlobalButton
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={submitting}
                  onClick={() => attachmentInputRef.current?.click()}
                >
                  Choose file
                </GlobalButton>
                {formData.attachment ? (
                  <>
                    <span className="cmr-file-upload__filename" title={formData.attachment.name}>
                      {formData.attachment.name}
                    </span>
                    <GlobalButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={submitting}
                      onClick={() => {
                        setFormField("attachment", null);
                        if (attachmentInputRef.current) attachmentInputRef.current.value = "";
                      }}
                    >
                      Remove
                    </GlobalButton>
                  </>
                ) : (
                  <span className="cmr-file-upload__empty">PDF, JPG, or PNG · max 5 MB</span>
                )}
              </div>
            </div>
          ) : null}
        </GlobalFormShell>
      </form>
    </div>
  );
};

export default CreateMaintenanceRequest;
